"use client";

import { useQueryClient } from "@tanstack/react-query";
import { OAuthProvider, SAMLAuthProvider, signInWithPopup } from "firebase/auth";
import { KeyRound, Mail } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { processUrlForDemo } from "@/lib/demo/demoUrlProcessing";
import env from "@/lib/env-public";
import { auth, forceInMemorySession } from "@/lib/firebase/firebase-client";
import { overrideAccountDomain } from "@/lib/testing/override-account-login";

import { withGuestPage } from "@/dashboard/components/auth/with-guest-page";
import {
  DottedScaffoldShell,
  useScaffoldContentRef,
} from "@/dashboard/components/onboarding/dotted-scaffold";
import { Button } from "@/dashboard/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/dashboard/components/ui/form";
import { Input } from "@/dashboard/components/ui/input";
import { Separator } from "@/dashboard/components/ui/separator";
import { CROSS_TENANT_QUERY_KEY_ROOTS } from "@/dashboard/constants/cross-tenant-query-keys";
import { useAuth } from "@/dashboard/contexts/auth-context";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import type { Account } from "@/dashboard/types/account";

type FormVals = { email: string; password: string };

function SignInPage({ accounts }: { accounts: Account[] }) {
  return (
    <DottedScaffoldShell>
      <SignInContent accounts={accounts} />
    </DottedScaffoldShell>
  );
}

function SignInContent({ accounts }: { accounts: Account[] }) {
  const scaffoldRef = useScaffoldContentRef();
  const searchParams = useSearchParams();
  const { signIn } = useAuth();
  const qc = useQueryClient();
  const [isSSOLoading, setIsSSOLoading] = useState(false);
  const [ssoError, setSSOError] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);

  const showAccountExistsBanner = searchParams.get("reason") === "account-exists";

  const domain = typeof window !== "undefined" ? window.location.hostname : "";
  let lookup = env.NEXT_PUBLIC_DEVELOPER_ACCOUNT_DOMAIN || domain;

  lookup = processUrlForDemo(lookup);
  lookup = overrideAccountDomain(lookup);

  const account = accounts.find(a => a.domain === lookup);
  const googleIdpTenantId = account?.googleIdpTenantId;

  const forcePasswordLogin =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("netic_login") === "true";
  const ssoProviderId = forcePasswordLogin ? undefined : account?.ssoProviderId;

  const form = useForm<FormVals>({ defaultValues: { email: "", password: "" } });

  async function onSubmit(values: FormVals) {
    if (!googleIdpTenantId) {
      setSignInError("Unable to determine your account. Please contact support.");
      return;
    }

    setSignInError(null);
    try {
      await signIn({ ...values, googleIdpTenantId });
      // Hard redirect for enterprise sign-in to avoid race with withGuestPage
      const redirectTo = searchParams.get("redirectTo");
      if (redirectTo?.startsWith("/enterprise/")) {
        window.location.href = redirectTo;
        return;
      }
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (
        msg.includes("auth/invalid-credential") ||
        msg.includes("auth/wrong-password") ||
        msg.includes("auth/user-not-found")
      ) {
        setSignInError("Incorrect email or password. Please try again.");
      } else if (msg.includes("auth/too-many-requests")) {
        setSignInError("Too many failed attempts. Please try again later.");
      } else if (msg.includes("auth/user-disabled")) {
        setSignInError("This account has been disabled. Please contact support.");
      } else {
        setSignInError("Unable to sign in. Please check your credentials and try again.");
      }
    }
  }

  async function onSSOSignIn() {
    if (!googleIdpTenantId || !ssoProviderId) {
      return;
    }

    try {
      setSSOError(null);
      setIsSSOLoading(true);
      auth.tenantId = googleIdpTenantId;
      const provider = ssoProviderId.startsWith("saml.")
        ? new SAMLAuthProvider(ssoProviderId)
        : new OAuthProvider(ssoProviderId);
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      await fetchJson("/api/dashboard/session", {
        method: "POST",
        body: JSON.stringify({ idToken }),
      });
      await forceInMemorySession();
      await qc.invalidateQueries({ queryKey: ["me"] });
      qc.removeQueries({ queryKey: ["call-source-status"] });
      // User change must evict cross-tenant keys (they survive tenant switches).
      for (const key of CROSS_TENANT_QUERY_KEY_ROOTS) {
        qc.removeQueries({ queryKey: [key] });
      }
    } catch (err) {
      setSSOError((err as Error).message ?? "SSO sign-in failed");
      setIsSSOLoading(false);
    }
  }

  return (
    <div
      ref={scaffoldRef}
      className="flex w-full max-w-[430px] flex-col gap-6 rounded-lg border border-border bg-white p-6"
    >
      <h1 className="text-2xl font-medium leading-8 text-foreground">Welcome to Netic</h1>

      {showAccountExistsBanner && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-800">
            An account with this email already exists. Please sign in below.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <Form {...form}>
          <form
            onSubmit={evt => void form.handleSubmit(onSubmit)(evt)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="Email"
                        className="pl-10"
                        required
                        {...field}
                        onChange={e => {
                          field.onChange(e);
                          setSignInError(null);
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="password"
                          placeholder="Password"
                          className="pl-10"
                          required
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            setSignInError(null);
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Link
                  href="/forgot-password"
                  className="text-sm text-muted-foreground underline hover:text-foreground"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {signInError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-700">{signInError}</p>
              </div>
            )}

            <Separator />

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                block
                className="h-10 bg-[#0d5a2b] text-white hover:bg-[#0a4a24]"
              >
                Log in
              </Button>

              <Button
                type="button"
                variant="outline"
                block
                className="h-10 text-muted-foreground"
                onClick={() => window.open("https://www.netic.ai/book-a-demo", "_blank")}
              >
                Sign up
              </Button>
            </div>

            {ssoProviderId && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-grow border-t border-border" />
                  <span className="text-sm text-muted-foreground">OR</span>
                  <div className="flex-grow border-t border-border" />
                </div>

                {ssoError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-700">{ssoError}</p>
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  block
                  className="h-10"
                  onClick={() => void onSSOSignIn()}
                  disabled={isSSOLoading}
                >
                  {isSSOLoading ? "Signing in..." : "Sign in with SSO"}
                </Button>
              </>
            )}
          </form>
        </Form>
      </div>
    </div>
  );
}

export default withGuestPage(SignInPage);
